import React, { useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import LayoutResizeHandle from './LayoutResizeHandle';
import {
  DEFAULT_LAYOUT_SETTINGS,
  LAYOUT_CONSTRAINTS,
  normalizeLayoutSettings,
} from '../../../utils/reportSidebarSync';

export const REPORT_EDITOR_PAGE_CONTENT_ZOOM = 0.8;

function flattenChildren(node) {
  if (node == null || node === false) return [];
  if (Array.isArray(node)) {
    return node.flatMap(flattenChildren);
  }
  if (React.isValidElement(node) && node.type === React.Fragment) {
    return React.Children.toArray(node.props.children).flatMap(flattenChildren);
  }
  return [node];
}

function stretchPaperSx() {
  return {
    display: 'flex',
    minWidth: 0,
    minHeight: 0,
    '& > .MuiPaper-root': {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
  };
}

function clampLayoutValue(key, value) {
  const constraints = LAYOUT_CONSTRAINTS[key];
  return Math.min(constraints.max, Math.max(constraints.min, Math.round(value)));
}

export default function ReportEditorLayout({
  sidebarDetached = false,
  leftColumn,
  rightColumn,
  commentOverlay,
  layoutSettings = DEFAULT_LAYOUT_SETTINGS,
  onLayoutChange,
  showSidebarPanels = true,
}) {
  const layout = normalizeLayoutSettings(layoutSettings);
  const gridRef = useRef(null);
  const leftStackRef = useRef(null);
  const dragStateRef = useRef(null);

  const containerSx = {
    zoom: REPORT_EDITOR_PAGE_CONTENT_ZOOM,
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: `calc((100vh - 200px) / ${REPORT_EDITOR_PAGE_CONTENT_ZOOM})`,
  };

  const leftItems = flattenChildren(leftColumn);
  const rightItems = flattenChildren(rightColumn);
  const patientCard = leftItems[0];
  const priorCard = showSidebarPanels ? leftItems[1] : null;
  const aiCard = showSidebarPanels ? leftItems[2] : null;
  const studyInfoCard = rightItems[0];
  const reportArea = rightItems[1];

  const rightColumnFraction = Math.max(1, 100 - layout.leftColumnWidthPercent);
  const gridColumns = `${layout.leftColumnWidthPercent}fr 8px ${rightColumnFraction}fr`;

  const handleColumnDragStart = useCallback((event) => {
    const grid = gridRef.current;
    if (!grid) return;
    const rect = grid.getBoundingClientRect();
    dragStateRef.current = {
      type: 'column',
      startX: event.clientX,
      startPercent: layout.leftColumnWidthPercent,
      containerWidth: rect.width,
    };
  }, [layout.leftColumnWidthPercent]);

  const handleColumnDrag = useCallback((event) => {
    const state = dragStateRef.current;
    if (!state || state.type !== 'column' || !state.containerWidth) return;
    const deltaPercent = ((event.clientX - state.startX) / state.containerWidth) * 100;
    const next = clampLayoutValue(
      'leftColumnWidthPercent',
      state.startPercent + deltaPercent
    );
    onLayoutChange?.({ leftColumnWidthPercent: next });
  }, [onLayoutChange]);

  const handleColumnDragEnd = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  const handlePriorAiDragStart = useCallback((event) => {
    const stack = leftStackRef.current;
    if (!stack) return;
    const rect = stack.getBoundingClientRect();
    dragStateRef.current = {
      type: 'priorAi',
      startY: event.clientY,
      startPercent: layout.priorStudiesHeightPercent,
      containerHeight: rect.height,
    };
  }, [layout.priorStudiesHeightPercent]);

  const handlePriorAiDrag = useCallback((event) => {
    const state = dragStateRef.current;
    if (!state || state.type !== 'priorAi' || !state.containerHeight) return;
    const deltaPercent = ((event.clientY - state.startY) / state.containerHeight) * 100;
    const next = clampLayoutValue(
      'priorStudiesHeightPercent',
      state.startPercent + deltaPercent
    );
    onLayoutChange?.({ priorStudiesHeightPercent: next });
  }, [onLayoutChange]);

  const handlePriorAiDragEnd = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  if (sidebarDetached) {
    return (
      <Box sx={containerSx}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            width: '100%',
            minHeight: 0,
            overflow: 'hidden',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr',
              gap: '12px',
              alignItems: 'stretch',
              flexShrink: 0,
            }}
          >
            <Box sx={stretchPaperSx()}>
              {patientCard}
            </Box>
            <Box sx={stretchPaperSx()}>
              {studyInfoCard}
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {reportArea}
          </Box>
        </Box>
        {commentOverlay}
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <Box
        ref={gridRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gridTemplateRows: 'auto 1fr',
          flex: 1,
          width: '100%',
          minHeight: 0,
          overflow: 'hidden',
          rowGap: '12px',
          columnGap: 0,
        }}
      >
        <Box sx={{ gridColumn: 1, gridRow: 1, ...stretchPaperSx() }}>
          {patientCard}
        </Box>

        <Box
          sx={{
            gridColumn: 2,
            gridRow: '1 / 3',
            display: 'flex',
            alignItems: 'stretch',
            minHeight: 0,
          }}
        >
          <LayoutResizeHandle
            orientation="vertical"
            onDragStart={handleColumnDragStart}
            onDrag={handleColumnDrag}
            onDragEnd={handleColumnDragEnd}
          />
        </Box>

        <Box sx={{ gridColumn: 3, gridRow: 1, ...stretchPaperSx() }}>
          {studyInfoCard}
        </Box>

        <Box
          ref={leftStackRef}
          sx={{
            gridColumn: 1,
            gridRow: 2,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {showSidebarPanels ? (
            <>
              <Box
                sx={{
                  flex: `${layout.priorStudiesHeightPercent} 1 0`,
                  minHeight: 120,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {priorCard}
              </Box>
              <LayoutResizeHandle
                orientation="horizontal"
                onDragStart={handlePriorAiDragStart}
                onDrag={handlePriorAiDrag}
                onDragEnd={handlePriorAiDragEnd}
              />
              <Box
                sx={{
                  flex: `${100 - layout.priorStudiesHeightPercent} 1 0`,
                  minHeight: 80,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {aiCard}
              </Box>
            </>
          ) : null}
        </Box>

        <Box
          sx={{
            gridColumn: 3,
            gridRow: 2,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {reportArea}
        </Box>
      </Box>

      {commentOverlay}
    </Box>
  );
}
